import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useEmpresa } from '../contexts/EmpresaContext';
import { Link } from 'react-router-dom';
import { colaboradoresService } from '../services/colaboradores.service';
import { vencimentosService } from '../services/vencimentos.service';
import { formatCNPJ } from '../utils/formatters';
import { useFeatureAccess } from '../hooks/useFeatureAccess';
import {
  CalendarDaysIcon,
  CheckBadgeIcon,
  ClockIcon,
  UserGroupIcon,
  BanknotesIcon,
  ArrowRightIcon,
  BuildingOffice2Icon,
  ShieldCheckIcon,
  SparklesIcon,
  KeyIcon,
  HeartIcon,
  DocumentCheckIcon,
} from '@heroicons/react/24/outline';

export default function Dashboard() {
  const { user } = useAuth();
  const { empresa, isIntegracoes, userRole } = useEmpresa();
  const { hasFeature, hasModulo, isLoading: featuresLoading } = useFeatureAccess();
  const [pendingCandidatesCount, setPendingCandidatesCount] = useState<number>(0);
  const [vencimentos3MesesCount, setVencimentos3MesesCount] = useState<number>(0);
  const [vencidosCount, setVencidosCount] = useState<number>(0);
  const [cardsAnimKey, setCardsAnimKey] = useState<number>(0);

  // Re-executa a animação de apresentação dos cards ao fechar ou concluir o tour
  useEffect(() => {
    const handleReplayCardsAnimation = () => {
      setCardsAnimKey((prev) => prev + 1);
    };

    window.addEventListener('tourFinishedAnimateDashboard', handleReplayCardsAnimation);
    return () => window.removeEventListener('tourFinishedAnimateDashboard', handleReplayCardsAnimation);
  }, []);

  useEffect(() => {
    let isMounted = true;
    if (isIntegracoes || (userRole !== 'ADMIN' && userRole !== 'AGENDADOR')) return;

    const loadCounts = async () => {
      try {
        const [candidatosCount, resumoVenc] = await Promise.all([
          colaboradoresService.countCandidatosPendentes().catch(() => 0),
          vencimentosService.getResumoDashboard().catch(() => ({ count_proximos_3_meses: 0, count_vencidos: 0, count_sem_data: 0 })),
        ]);
        if (isMounted) {
          if (typeof candidatosCount === 'number') {
            setPendingCandidatesCount(candidatosCount);
          }
          if (resumoVenc) {
            if (typeof resumoVenc.count_proximos_3_meses === 'number') {
              setVencimentos3MesesCount(resumoVenc.count_proximos_3_meses);
            }
            if (typeof resumoVenc.count_vencidos === 'number') {
              setVencidosCount(resumoVenc.count_vencidos);
            }
          }
        }
      } catch {
        // Silently catch if not loaded yet
      }
    };

    if (empresa?.id) {
      loadCounts();
    }

    return () => {
      isMounted = false;
    };
  }, [empresa?.id, isIntegracoes, userRole]);

  const allOperationalCards = [
    {
      title: 'Organização',
      description: 'Estrutura hierárquica por setores e cargos, conformidade de SST e movimentações.',
      href: '/organizacao',
      tourId: 'organizacao',
      icon: BuildingOffice2Icon,
      accentColor: 'from-violet-500 to-indigo-600',
      iconBg: 'bg-indigo-50 text-indigo-600 border-indigo-200',
      badge: 'Estrutura & Pessoas',
      badgeColor: 'bg-indigo-50 text-indigo-700 border-indigo-200',
      visible: hasModulo('gestao_organizacional') && (hasFeature('colaboradores') || hasFeature('solicitantes') || hasFeature('organizacao')),
    },
    {
      title: 'Agendar e Acompanhar Exames',
      description: 'Agende exames admissionais, periódicos ou demissionais para colaboradores ou novos candidatos.',
      href: '/agendamento/cadastro',
      tourId: 'agendar-exames',
      icon: CalendarDaysIcon,
      accentColor: 'from-emerald-500 to-teal-600',
      iconBg: 'bg-emerald-50 text-emerald-600 border-emerald-200',
      badge: 'Agendamento Rápido',
      badgeColor: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      visible: hasModulo('gestao_exames') && (hasFeature('exames') || hasFeature('clinical_exams') || hasFeature('online_management')),
    },
    {
      title: 'Compliance & Regulamentação',
      description: 'Acesse procurações e-CAC, contratos corporativos, PGR, PCMSO e laudos de SST.',
      href: '/compliance',
      tourId: 'compliance',
      icon: ShieldCheckIcon,
      accentColor: 'from-blue-500 to-indigo-600',
      iconBg: 'bg-blue-50 text-blue-600 border-blue-200',
      badge: 'Conformidade SST',
      badgeColor: 'bg-blue-50 text-blue-700 border-blue-200',
      visible: hasModulo('gestao_compliance'),
    },
    {
      title: 'Gestão de Absenteísmo',
      description: 'Envie atestados médicos em lote, acompanhe validação forense IA, Fator Bradford e aprove abonos para a folha.',
      href: '/saude/atestados',
      tourId: 'saude-atestados',
      icon: DocumentCheckIcon,
      accentColor: 'from-emerald-500 to-teal-600',
      iconBg: 'bg-emerald-50 text-emerald-600 border-emerald-200',
      badge: 'Absenteísmo & RH',
      badgeColor: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      visible:
        hasModulo('gestao_absenteismo') ||
        hasModulo('gestao_atestados') ||
        hasFeature('atestados') ||
        hasFeature('absenteeism'),
    },
    {
      title: 'Saúde Corporativa & Inteligência de dados',
      description: 'Prontuário clínico longitudinal ocupacional, histórico integrado de ASOs, exames complementares e perfil epidemiológico.',
      href: '/saude/dashboard',
      tourId: 'saude-dashboard',
      icon: HeartIcon,
      accentColor: 'from-rose-500 to-pink-600',
      iconBg: 'bg-rose-50 text-rose-600 border-rose-200',
      badge: 'Prontuário & PCMSO',
      badgeColor: 'bg-rose-50 text-rose-700 border-rose-200',
      visible:
        hasModulo('saude_corporativa') ||
        hasFeature('prontuario_clinico'),
    },
    {
      title: 'Gestão de EPIs',
      description: 'Controle de entregas com biometria facial, vigência, estoque Kardex e devoluções NR-06.',
      href: '/epis',
      icon: ShieldCheckIcon,
      accentColor: 'from-emerald-500 to-teal-600',
      iconBg: 'bg-emerald-50 text-emerald-600 border-emerald-200',
      badge: 'NR-06 Digital',
      badgeColor: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      visible: hasModulo('gestao_epis') && hasFeature('epis'),
    },
    {
      title: 'Comissão de CIPA (NR-5)',
      description: 'Acompanhe o quadro de cipeiros, estabilidade CLT, atas de reunião e processos eleitorais com urna digital.',
      href: '/cipa',
      tourId: 'cipa',
      icon: UserGroupIcon,
      accentColor: 'from-teal-500 to-emerald-600',
      iconBg: 'bg-teal-50 text-teal-600 border-teal-200',
      badge: 'NR-5 Digital',
      badgeColor: 'bg-teal-50 text-teal-700 border-teal-200',
      visible: hasModulo('gestao_cipa'),
    },
    {
      title: 'Aprovar Candidatos',
      description: 'Valide ASOs de admissão, aprove contratações e libere a transmissão ao eSocial.',
      href: '/candidatos-aprovacao',
      tourId: 'aprovar-candidatos',
      icon: CheckBadgeIcon,
      accentColor: 'from-amber-500 to-orange-600',
      iconBg: 'bg-amber-50 text-amber-600 border-amber-200',
      badge: pendingCandidatesCount > 0 ? `${pendingCandidatesCount} pendente(s)` : 'Admissões',
      badgeColor: pendingCandidatesCount > 0 ? 'bg-amber-100 text-amber-800 border-amber-300 font-semibold' : 'bg-gray-100 text-gray-700 border-gray-200',
      notificationCount: pendingCandidatesCount,
      notificationColor: 'bg-rose-600',
      visible: hasModulo('gestao_organizacional') && (hasFeature('colaboradores') || hasFeature('candidatos')),
    },
    {
      title: 'Periódicos a Vencer',
      description: 'Acompanhe a agenda preventiva de exames periódicos e evite atrasos na conformidade.',
      href: '/vencimentos-exames',
      tourId: 'periodicos',
      icon: ClockIcon,
      accentColor: 'from-purple-500 to-violet-600',
      iconBg: 'bg-purple-50 text-purple-600 border-purple-200',
      badge:
        vencidosCount > 0 && vencimentos3MesesCount > 0
          ? `${vencidosCount} vencido(s) • ${vencimentos3MesesCount} próx. 90d`
          : vencidosCount > 0
          ? `${vencidosCount} vencido(s)`
          : vencimentos3MesesCount > 0
          ? `${vencimentos3MesesCount} nos próx. 90 dias`
          : 'Controle de Prazos',
      badgeColor:
        vencidosCount > 0
          ? 'bg-rose-100 text-rose-800 border-rose-300 font-semibold'
          : vencimentos3MesesCount > 0
          ? 'bg-amber-100 text-amber-800 border-amber-300 font-semibold'
          : 'bg-purple-50 text-purple-700 border-purple-200',
      notificationCount: vencidosCount + vencimentos3MesesCount,
      notificationColor: vencidosCount > 0 ? 'bg-rose-600' : 'bg-amber-600',
      visible: hasModulo('gestao_exames') && (hasFeature('vencimentos_exames') || hasFeature('exames') || hasFeature('clinical_exams')),
    },
    {
      title: 'Usuários da Empresa',
      description: 'Gerencie os solicitantes e colaboradores com permissão de acesso nesta empresa.',
      href: '/solicitantes',
      tourId: 'usuarios',
      icon: UserGroupIcon,
      accentColor: 'from-cyan-500 to-blue-600',
      iconBg: 'bg-cyan-50 text-cyan-600 border-cyan-200',
      badge: 'Gestão de Acessos',
      badgeColor: 'bg-cyan-50 text-cyan-700 border-cyan-200',
      visible: hasModulo('gestao_organizacional') && hasFeature('solicitantes'),
    },
    {
      title: 'Faturas e Boletos',
      description: 'Consulte os faturamentos mensais, notas fiscais e boletos emitidos para o contrato.',
      href: '/faturamento',
      icon: BanknotesIcon,
      accentColor: 'from-slate-600 to-gray-800',
      iconBg: 'bg-slate-50 text-slate-700 border-slate-200',
      badge: 'Faturamento Mensal',
      badgeColor: 'bg-slate-100 text-slate-700 border-slate-200',
      visible: hasModulo('financeiro') && (hasFeature('faturamento') || hasFeature('financeiro')),
    },
    {
      title: 'Integrações & API',
      description: 'Gere chaves de integração REST, configure firewall de IPs e consulte a documentação técnica.',
      href: '/integracoes',
      tourId: 'integracoes',
      icon: KeyIcon,
      accentColor: 'from-emerald-600 to-teal-700',
      iconBg: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      badge: 'API REST & Tokens',
      badgeColor: 'bg-emerald-50 text-emerald-800 border-emerald-200',
      visible: isIntegracoes && hasModulo('integracoes'),
    },
  ];

  const operationalCards = allOperationalCards.filter((card) => {
    if (!card.visible) return false;
    if (isIntegracoes) {
      return card.href === '/integracoes';
    }
    if (card.href === '/integracoes') {
      return false;
    }
    if (userRole === 'AGENDADOR') {
      return [
        '/organizacao',
        '/agendamento/cadastro',
        '/candidatos-aprovacao',
        '/vencimentos-exames',
        '/solicitantes',
      ].includes(card.href);
    }
    if (userRole === 'TECNICO') {
      return [
        '/compliance',
        '/saude/atestados',
        '/saude/dashboard',
        '/epis',
        '/cipa',
      ].includes(card.href);
    }
    if (userRole === 'FINANCEIRO') {
      return ['/faturamento'].includes(card.href);
    }
    return true;
  });

  return (
    <div className="space-y-8 animate-fadeIn">
      {/* Hero / Header Card */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200/80 p-6 md:p-8 relative overflow-hidden">
        <div className="absolute top-0 right-0 -mt-8 -mr-8 w-64 h-64 bg-gradient-to-br from-emerald-100/40 to-teal-100/20 rounded-full blur-3xl pointer-events-none" />
        
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 relative z-10">
          <div>
            <div className="text-sm text-emerald-700 font-semibold mb-1">
              <span>Portal de Saúde Ocupacional e Segurança do Trabalho</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight">
              Olá, {user?.name || 'Gestor de RH'}!
            </h1>
            <div className="flex flex-wrap items-center gap-3 mt-1.5">
              <p className="text-sm md:text-base text-gray-500 max-w-2xl">
                Bem-vindo ao seu painel centralizado. Acesse abaixo os atalhos operacionais rápidos da sua empresa.
              </p>
              <button
                type="button"
                onClick={() => window.dispatchEvent(new CustomEvent('restartCustomerTour'))}
                className="hidden lg:inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 hover:text-emerald-800 border border-emerald-200/80 rounded-full text-xs font-semibold shadow-2xs transition-all cursor-pointer hover:scale-105 active:scale-95"
                title="Iniciar o tour guiado dos módulos"
              >
                <SparklesIcon className="w-3.5 h-3.5 text-emerald-600" />
                <span>Tour interativo</span>
              </button>
            </div>
          </div>

          {empresa && (
            <div className="flex items-center bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 space-x-3 self-start md:self-auto">
              <div className="p-2 bg-white rounded-lg border border-gray-200 shadow-2xs">
                <BuildingOffice2Icon className="w-5 h-5 text-gray-700" />
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Empresa Ativa
                </p>
                <p className="text-sm font-bold text-gray-900 leading-tight">
                  {empresa.nome}
                </p>
                {empresa.cnpj && (
                  <p className="text-xs text-gray-500 font-mono">
                    {formatCNPJ(empresa.cnpj)}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 6 Essential Operational Shortcuts */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-900 tracking-tight">
            Ações Rápidas
          </h2>
          <span className="text-xs font-medium text-gray-500">
            Foco operacional direto
          </span>
        </div>

        {featuresLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div
                key={i}
                className="h-44 rounded-2xl bg-gray-100/70 border border-gray-200/60 animate-pulse"
              />
            ))}
          </div>
        ) : operationalCards.length === 0 ? (
          <div className="bg-white rounded-2xl p-8 border border-gray-200/80 text-center max-w-xl mx-auto shadow-xs">
            <div className="w-12 h-12 mx-auto mb-3 bg-amber-50 rounded-xl flex items-center justify-center text-amber-600 border border-amber-200">
              <KeyIcon className="w-6 h-6" />
            </div>
            <h3 className="text-base font-bold text-gray-900 mb-1">
              Nenhuma ação rápida disponível
            </h3>
            <p className="text-sm text-gray-500">
              {isIntegracoes
                ? 'O módulo de Integrações & API não está habilitado no contrato desta empresa. Solicite a inclusão comercial do módulo para liberar o acesso.'
                : 'Não há atalhos operacionais liberados para o seu perfil nesta empresa.'}
            </p>
          </div>
        ) : (
          <div key={cardsAnimKey} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-fadeIn">
            {operationalCards.map((card, index) => {
              const Icon = card.icon;
              return (
                <Link
                  key={`${card.href}-${cardsAnimKey}`}
                  to={card.href}
                  data-tour={card.tourId}
                  style={{ animationDelay: `${index * 80}ms` }}
                  className="animate-dashboard-card group bg-white rounded-2xl p-6 border border-gray-200/80 shadow-xs hover:shadow-xl hover:border-gray-300 hover:-translate-y-1.5 transition-all duration-300 ease-out flex flex-col justify-between relative overflow-hidden"
                >
                  {/* Top decorative line on hover */}
                  <div
                    className={`absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r ${card.accentColor} opacity-0 group-hover:opacity-100 transition-all duration-300`}
                  />

                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center border shadow-2xs ${card.iconBg} group-hover:scale-110 group-hover:shadow-md transition-all duration-300 relative`}>
                        <Icon className="w-6 h-6 transition-transform duration-300 group-hover:rotate-3" />
                        {card.notificationCount != null && card.notificationCount > 0 && (
                          <span
                            className={`absolute -top-1.5 -right-1.5 flex h-5 min-w-[20px] px-1.5 items-center justify-center rounded-full ${card.notificationColor || 'bg-rose-600'} text-[11px] font-bold text-white shadow-md ring-2 ring-white animate-pulse`}
                            title={`${card.notificationCount} pendência(s)`}
                          >
                            {card.notificationCount}
                          </span>
                        )}
                      </div>
                      {card.badge && (
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs border ${card.badgeColor} transition-transform duration-200 group-hover:scale-105`}>
                          {card.badge}
                        </span>
                      )}
                    </div>

                    <h3 className="text-lg font-bold text-gray-900 group-hover:text-emerald-600 transition-colors duration-200">
                      {card.title}
                    </h3>
                    <p className="mt-2 text-sm text-gray-500 leading-relaxed">
                      {card.description}
                    </p>
                  </div>

                  <div className="mt-6 pt-4 border-t border-gray-100 flex items-center justify-between text-xs font-semibold text-gray-500 group-hover:text-emerald-600 transition-colors duration-200">
                    <span>Acessar funcionalidade</span>
                    <ArrowRightIcon className="w-4 h-4 transform group-hover:translate-x-1.5 transition-transform duration-300" />
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
